<template>
    <b-container id="question-main-layout" fluid class=" px-0">

        <!-- Question & Question info 영역 -->
        <div class="px-3 px-md-5 py-4 mb-2">
            <div class="f-85 gray-550 float-right text-right" style="max-width: 400px;">
                {{ campaign_name }}
            </div>
            <h5 v-show="!isFieldEmpty(question_name)"
                :class="[{'mb-3': !isFieldEmpty(question_infobox)}, 'fw-600']"
            >
                {{ question_name }}
            </h5>
            <div v-show="!isFieldEmpty(question_infobox)"
                 id="question_infobox"
                 :class="[{ 'f-90 p-2 px-3 gray-600 bgray-200 rounded': !isFieldEmpty(question_infobox) }]"
                 v-html="question_infobox"
            >
            </div>
        </div>

        <!-- Sub-component 영역 -->
        <div v-if="subs_error" class="px-3 px-md-5">
            <b-alert variant="danger" show class="d-flex flex-wrap align-items-center justify-content-between">
                <span class="mr-3">{{ $t('문항을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.') }}</span>
                <b-button variant="outline-danger" size="sm" class="my-1" @click="retryLoadSubs">
                    <fa icon="redo" class="mr-1"></fa>
                    {{ $t('다시 불러오기') }}
                </b-button>
            </b-alert>
        </div>
        <div v-else-if="!subs_loaded" class="text-center">
            <b-spinner class="mt-5 grey"></b-spinner>
            <div v-if="show_download_alt_pdf">
                <b-alert variant="warning" show class="mx-4 mt-4">
                    <div class="d-flex flex-column align-items-center justify-content-center">
                        <div class="d-flex align-items-center justify-content-center">
                            <b-icon-exclamation-triangle-fill font-scale="1.5"
                                                              class="mr-3"></b-icon-exclamation-triangle-fill>

                            <div class="flex-grow-1">
                                <div class="text-left">
                                    {{ $t('문항 로딩 시간이 20초를 초과했습니다.') }}

                                </div>
                                <div class="text-left">
                                    {{ $t('문제가 지속해서 발생한다면 PDF를 다운로드하여 질문에 답변을 하신 뒤,') }}
                                </div>
                                <div class="text-left">
                                    {{ $t('아래 메일로 답변지를 보내주십시오.') }}
                                </div>
                            </div>
                        </div>
                        <b-input-group class="mt-3 width-calc50">
                            <b-input-group-prepend>
                                <b-button href="mailto:info@qesg.co.kr" variant="outline-secondary"
                                          type="button"
                                >
                                    <b-icon-envelope-fill></b-icon-envelope-fill>
                                </b-button>
                            </b-input-group-prepend>

                            <b-form-input
                                value="info@qesg.co.kr"
                                readonly
                                class="text-left font-weight-bold"
                            ></b-form-input>

                            <b-input-group-append>
                                <b-button variant="outline-secondary" @click="copyEmail">{{ $t('복사') }}</b-button>
                            </b-input-group-append>
                        </b-input-group>
                        <div v-if="alt_pdf_url" class="mt-3">
                            <b-button type="download" :download="`${question_name}.pdf`"
                                      :href="alt_pdf_url">
                                <b-icon-download class="mr-2"></b-icon-download>
                                {{ $t('PDF 다운로드') }}
                            </b-button>
                        </div>
                    </div>
                </b-alert>
            </div>
        </div>
        <div v-else-if="subs_loaded && subs.length > 0" class="px-3 px-md-5">
            <!-- 입력이 잠긴 이유를 상시 안내한다 (툴팁은 휴대폰에서 볼 수 없다) -->
            <template v-if="mode !== 'public'">
                <b-alert v-if="is_submitted" show variant="info" class="f-90">
                    <fa icon="info-circle" class="mr-1"></fa>{{ $t('제출되어 수정할 수 없습니다.') }}
                </b-alert>
                <b-alert v-else-if="is_completed === 'true' && !is_disabled_by_parent" show variant="info" class="f-90">
                    <fa icon="info-circle" class="mr-1"></fa>{{ $t('완료 처리된 문항입니다. 수정하려면 아래 완료 체크를 해제해 주세요.') }}
                </b-alert>
            </template>
            <!-- 캠페인별 위젯 (custom hyundai-v2/hmc-theme Questions 이관) -->
            <div v-if="show_management_level" class="mb-3">
                <ManagementLevelTable :code-id="currentCodeId"
                                      :company-id="target_company_id"
                                      :campaign-id="widget_campaign_id"
                                      :project-code="$route.query.pc || custom_project_code"
                                      :sub-codes="currentSubCodes"
                                      :subs-data="subs"
                />
            </div>
            <div v-if="themeScoredSubCodes.length > 0" class="mb-3">
                <ThemeLevelSummary :company-id="target_company_id"
                                   :campaign-id="widget_campaign_id"
                                   :project-code="$route.query.pc || custom_project_code"
                                   :sub-codes="themeScoredSubCodes"
                                   :subs-data="subs"
                />
            </div>
            <sub-component v-for="item in subs"
                           :key="item.sub_code"
                           :ref="'sub_components'"
                           :sub_type="item.sub_type"
                           :item="item"
                           :last_save_state="map_save_state_msg[item.sub_code]"
                           :is_disabled_by_parent="is_disabled_by_parent"
                           @update-pipeline="(msg) => handleUpdatePipeline(item.sub_code, msg)"
            >
            </sub-component>
        </div>
        <div v-else-if="subs.length === 0" class="px-3 px-md-5">
            <b-card class="bgray-200"> {{ $t('다음 질문으로 이동하여 주십시오.') }}</b-card>
        </div>

        <!-- 완료 체크 및 문항 이동 영역 -->
        <div class="px-3 px-md-5 py-5 mb-5">
            <template v-if="subs.length > 0 && is_completed !== 'true' && is_disabled_by_parent !== true">
                <div class="f-90 mr-2 red indent-lg text-right">
                    <fa icon="info-circle" class="mr-1"></fa>
                    <span v-if="$i18n.locale === 'ko'">
                        답변을 완료하셨다면 <b>'{{ question_name }} 완료하기'</b> 체크 후 다음으로 이동해주세요.
                    </span>
                    <span v-else-if="$i18n.locale === 'en'">
                        If you have completed the answer, check the 'Complete' and move to the ‘Next’.
                    </span>
                    <span v-else-if="$i18n.locale === 'ca'">
                        如果您已完成答案，请选中 <b>完成 {{ question_name }}</b> 并移至下一个。
                    </span>
                </div>
            </template>

            <div class="d-flex align-items-center justify-content-end mt-2">
                <template v-if="subs.length > 0 && is_completing">
                    <div class="border border-color-blue rounded p-2">
                        <b-spinner class="blue"></b-spinner>
                    </div>
                </template>
                <template v-else-if="subs.length > 0 && !is_completing && mode !== 'public'">
                    <div :class="[is_completed === 'true' ? 'border border-color-blue' : 'border border-color-red', 'cursor rounded p-2']">
                        <b-form-checkbox v-model="is_completed"
                                         class="d-inline blue-check"
                                         value="true"
                                         unchecked-value="false"
                                         :disabled="is_disabled_by_parent"
                                         @change="toggleProgress"
                        >
                            <span :class="is_completed === 'true' ? 'blue' : 'red'">
                                {{
                                    is_completed == 'true' ? $t('완료됨') : $t("완료하기", {question_name: question_name})
                                }}
                            </span>
                        </b-form-checkbox>
                    </div>
                </template>

                <b-button class="ml-2 py-2"
                          variant="dark-border"
                          :disabled="is_completing || is_subs_loading"
                          @click.stop.prevent="goForward"
                >
                    <b-spinner v-if="is_subs_loading" small class="mr-1"></b-spinner>
                    {{ $t('다음') }}
                    <fa icon="caret-right" class="ml-2"></fa>
                </b-button>
            </div>
            <div v-if="subs.length > 0 && mode !== 'public'" class="f-80 gray-600 text-right mt-1">
                {{ is_disabled_by_parent ? $t('이미 제출되어 완료를 해제할 수 없습니다.') : $t('최종 제출 전까지 완료를 해제하고 답변을 수정할 수 있습니다.') }}
            </div>
        </div>
    </b-container>
</template>

<script>
import {mapActions, mapState} from 'vuex';
import SubComponent from '@core_components/client/components/subs/SubComponent';
import dayjs from 'dayjs';
import ManagementLevelTable from '@shared_components/common/ManagementLevelTable.vue';
import ThemeLevelSummary from '@shared_components/common/ThemeLevelSummary.vue';
import themeSubtopics from '@shared/mixin/report/raw-data/theme-subtopics.json';
import mltCampaignConfig from '@shared/mixin/report/raw-data/code_level_config_hyundai_v2.json';

export default {
    name: 'Questions',
    props: {
        question_code: {
            type: String,
            default: ''
        }
    },
    components: {
        SubComponent,
        ManagementLevelTable,
        ThemeLevelSummary
    },
    data() {
        return {
            template_name: '',
            is_completing: false,
            map_save_state_msg: {},
            loading_time: null,
            loading_time_interval: null,
            show_download_alt_pdf: false,
            alt_pdf_url: null,
        };
    },
    computed: {
        ...mapState('questions', ['subs', 'subs_loaded', 'subs_error']),
        ...mapState(['campaign_in_progress', 'question_in_progress', 'question_in_progress_loaded', 'user_id', 'template_in_progress', 'template_in_progress_loaded', 'mode']),
        ...mapState('industry_campaigns', ['custom_project_code', 'custom_template', 'custom_template_loaded']),
        // 평가 대상은 URL(cmp/cpn)이 단일 출처 — store 는 화면 표시(이름·상태)에만 쓴다
        target_campaign_id() {
            return this.$route.query.cmp;
        },
        target_company_id() {
            return this.$route.query.cpn;
        },
        is_subs_loading() {
            return !this.subs_loaded && !this.subs_error;
        },
        is_submitted() {
            return ['in_assessment', 'completed'].includes(this.campaign_in_progress.status_code);
        },
        // ── 캠페인별 위젯 게이팅 (custom hyundai-v2/hmc-theme 앱 이관) ──
        // custom 과 동일하게 데이터 기반 자기게이팅만 쓴다 (MLT=캠페인 config 키, TLS=테마 sub_code 화이트리스트).
        // 캠페인 상수/렌더러 키 게이트를 덧씌우면 테스트 캠페인(예: 559)처럼 상수 밖 캠페인에서 위젯이 사라진다 (2026-09-03).
        widget_campaign_id() {
            return this.$route.query.cmp || this.campaign_in_progress?.campaign_id;
        },
        currentCodeId() {
            const name = this.question_in_progress?.question_name || '';
            const match = name.match(/^([A-Z]-\d+-\d+)/);
            return match ? match[1] : '';
        },
        currentSubCodes() {
            if (!this.subs || this.subs.length === 0) return [];
            return this.subs.map(sub => sub.sub_code).filter(Boolean);
        },
        // MLT 는 내부 empty 상태가 어정쩡해서 config 존재를 페이지 레벨에서 게이트 (조사 권고)
        show_management_level() {
            if (this.mode === 'public') return false;
            return !!this.currentCodeId
                && !!mltCampaignConfig[String(this.widget_campaign_id || '')];
        },
        themeScoredSubCodes() {
            if (this.mode === 'public') return [];
            if (!this.subs || this.subs.length === 0) return [];
            return this.subs.map(s => s.sub_code).filter(code => code && themeSubtopics.byCode && themeSubtopics.byCode[code]);
        },
        // 번역이 비어 있으면 한국어 원문을 보인다 (제목이 통째로 사라지지 않게)
        campaign_name() {
            const c = this.campaign_in_progress;
            return this.localizedOrKo(c.campaign_name, c.campaign_name_e, c.campaign_name_c);
        },
        question_name() {
            const q = this.question_in_progress;
            return this.localizedOrKo(q.question_name, q.question_name_e, q.question_name_c);
        },
        question_infobox() {
            const q = this.question_in_progress;
            return this.localizedOrKo(q.question_infobox, q.question_infobox_e, q.question_infobox_c);
        },
        question_contents() {
            return this.question_in_progress.question_contents;
        },
        question_comment() {
            return this.question_in_progress.comment;
        },
        is_completed: {
            get() {
                return this.question_in_progress.is_completed;
            },
            set(newVal) {
                let temp_obj = {...this.question_in_progress};
                temp_obj.is_completed = newVal;
                this.$store.commit('update_question_in_progress', temp_obj);
            }
        },
        is_disabled_by_parent() {
            if (this.campaign_in_progress.campaign_state === '답변중' && this.campaign_in_progress.status_code === 'in_progress') return false;
            else if (this.campaign_in_progress.campaign_state === '생성/편집' && this.campaign_in_progress.status_code == 'in_progress') return false;
            else return true;
        },
        route_question_key() {
            return `${this.$route.params.question_code}|${this.template_in_progress_loaded}`;
        },
        // 대분류 아래 문항(잎 노드)을 사이드바 순서대로 (중분류가 곧 문항인 템플릿도 포함)
        another_question_list() {
            const leaves = [];
            const walk = nodes => (nodes || []).forEach(node => {
                if (node.children && node.children.length) walk(node.children);
                else leaves.push(node);
            });
            (this.template_in_progress.template_contents || []).forEach(category => walk(category.children));
            return leaves;
        },
        // 로딩 중인 문항이 바뀌면 20초 대체 안내 타이머를 새로 시작한다
        subs_loading_key() {
            return this.is_subs_loading ? (this.question_in_progress.question_code || '-') : '';
        }
    },
    watch: {
        // URL(question_code)이 단일 진실 원천: 뒤로/앞으로가기·새로고침 복원 시 본문을 URL 문항으로 맞춘다
        route_question_key: {
            immediate: true,
            handler() {
                this.syncQuestionWithRoute();
            }
        },
        // 로딩 중일 때만 20초 대체 안내 타이머를 돌린다 (완료·오류·다른 문항이면 타이머와 안내를 초기화)
        subs_loading_key: {
            immediate: true,
            handler(loading_key) {
                this.resetLoadingCheckProcess();
                if (!loading_key) return;
                this.map_save_state_msg = {};

                this.loading_time = dayjs();
                this.loading_time_interval = setInterval(() => {
                    let now = dayjs();
                    let diff = now.diff(this.loading_time, 'second');
                    if (diff > 20 && !this.show_download_alt_pdf) {
                        this.show_download_alt_pdf = true;
                        this.checkAltPdf();
                    }
                }, 1000); // 1초마다 체크
            }
        }
    },
    created() {
    },
    beforeDestroy() {
        this.$emit('toggle-writable', true);
        this.clearPrevAttachments();

        this.resetLoadingCheckProcess();
    },
    methods: {
        ...mapActions('attachments', ['clearPrevAttachments']),
        resetLoadingCheckProcess(){
            clearInterval(this.loading_time_interval);
            this.loading_time_interval = null;
            this.loading_time = null;
            this.show_download_alt_pdf = false;
            this.alt_pdf_url = null;
        },
        localizedOrKo(ko, en, ca) {
            const localized = {en, ca}[this.$i18n.locale];
            return localized || ko;
        },
        // 대체 PDF 는 일부 문항에만 있다. 없는 파일은 SPA 가 index.html 을 돌려주므로 실제 PDF 일 때만 버튼을 보인다
        altPdfUrl() {
            return `/questions/${this.$route.params.question_code}_${this.$i18n.locale}.pdf`;
        },
        async checkAltPdf() {
            const url = this.altPdfUrl();
            try {
                const res = await fetch(url, {method: 'HEAD'});
                // 확인하는 사이 다른 문항으로 옮겼으면 버린다
                if (res.ok && (res.headers.get('content-type') || '').includes('pdf') && this.show_download_alt_pdf && url === this.altPdfUrl()) {
                    this.alt_pdf_url = url;
                }
            } catch (error) {
                // 확인 못 하면 버튼을 숨긴 채 둔다
            }
        },
        retryLoadSubs() {
            this.pageGo(this.question_in_progress);
        },
        async toggleProgress() {
            if (await this.saveQuestionInProgress()) {
                await this.saveProgressLog();
            }
        },
        async saveQuestionInProgress() {
            this.is_completing = true;
            try {
                await this.$store.dispatch('saveQuestionInProgress', {
                    question: this.question_in_progress,
                    campaign_id: this.target_campaign_id,
                    company_id: this.target_company_id
                });
                return true;
            } catch (error) {
                // 저장 실패 시 체크를 원래 값으로 되돌린다
                this.is_completed = this.is_completed === 'true' ? 'false' : 'true';
                if (error?.response?.status === 401) return false; // 인터셉터가 로그인으로 보낸다
                this.$EventBus.$emit('toast-failure', this.$t('저장 실패'), this.$t('완료 상태를 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.'));
                return false;
            } finally {
                this.is_completing = false;
            }
        },
        async syncQuestionWithRoute() {
            const code = this.$route.params.question_code;
            if (!code || !this.template_in_progress_loaded || this.question_in_progress.question_code === code) return;
            const question = this.another_question_list.find(item => item.question_code === code);
            if (question) await this.pageGo(question);
        },
        async saveProgressLog() {
            try {
                let progress_rate = this.$store.getters.getProgressRate;
                // const path = this.$store.state.backend_host + '/save_progress_log'
                const path = '/save_progress_log';

                let formData = new FormData();
                formData.append('user_id', this.user_id);
                formData.append('campaign_id', this.target_campaign_id);
                formData.append('company_id', this.target_company_id);
                formData.append('question_code', this.question_code);
                formData.append('is_completed', this.is_completed);
                formData.append('progress_rate', progress_rate);

                await this.$axios.post(path, formData);
            } catch (error) {
                // 진행 기록은 화면 동작과 무관해 실패해도 조용히 넘긴다 (401 은 인터셉터가 로그인으로 보낸다)
            }

        },
        async goForward() {
            if (this.is_subs_loading) return; // 이동 중 다시 누르면 문항을 건너뛴다
            let idx = this.another_question_list.findIndex(item => item.question_code == this.question_in_progress.question_code);
            if (idx > -1 && idx < this.another_question_list.length - 1) {
                await this.pageGo(this.another_question_list[idx + 1]);
            } else {
                await this.$showModal({
                    type: 'alert',
                    state: 'info',
                    message: this.$t('마지막 문항입니다. 전체 답변을 완료하신 경우 우측의 \'최종 제출하기\' 버튼을 클릭하여 제출해주세요.')
                });
            }
        },
        async pageGo(question) {
            this.map_save_state_msg = {};
            let temp_question = {...question};
            if (typeof (question.question_contents) === 'string') {
                // string 형태로 가지고 있는 것 parsing 해야함
                try {
                    temp_question.question_contents = JSON.parse(question.question_contents);
                } catch (error) {
                    this.$EventBus.$emit('toast-failure', this.$t('알림'), this.$t('문항을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'));
                    return;
                }
            }
            this.$store.commit('update_question_in_progress', temp_question);

            const {pc, cmp, cpn} = this.$route.query;
            const started_at = this.$route.fullPath;

            try {
                const is_latest = await this.$store.dispatch('questions/readSubs', {
                    code: temp_question.question_code,
                    contents: temp_question.question_contents,
                    company_id: cpn,
                    campaign_id: cmp,
                    path: 'questions'
                });
                // 그 사이 다른 문항을 눌렀으면 이 문항으로 되돌아가지 않는다
                if (is_latest === false) return;
            } catch (error) {
                // 실패해도 대상 문항으로 이동해 제목·URL·목록을 맞춘다(본문 자리에 다시 불러오기 버튼이 뜬다)
                this.$EventBus.$emit('toast-failure', this.$t('알림'), this.$t('문항을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'));
            }

            // 로딩 중 사용자가 다른 화면(안내·다른 문항)으로 옮겼으면 이 문항으로 끌고 오지 않는다
            if (this.$route.fullPath !== started_at) return;
            if (temp_question.question_code != this.$route.params.question_code) {
                this.$nextTick(() => {
                    this.$router.push({
                        name: 'Questions',
                        query: {pc, cmp, cpn},
                        params: {question_code: temp_question.question_code}
                    });
                });
            }
        },
        makeCompleted() {
            if (this.campaign_in_progress.campaign_state !== '답변중' || this.campaign_in_progress.campaign_state !== '생성완료') {
                this.is_completed = 'true';
            }
        },
        // 한 답변이 파이프라인을 바꾸면 문항 전체를 다시 만들지 않고, 각 sub 가 이 알림을 듣고 자기 표시만 갱신한다
        // (다시 만들면 입력 위치·열린 도움말이 사라지고 sub 마다 조회가 다시 나갔다)
        handleUpdatePipeline(sub_code, last_save_state) {
            this.$set(this.map_save_state_msg, sub_code, last_save_state);
            this.$EventBus.$emit('pipeline-updated');
        },
        copyEmail(){
            navigator.clipboard.writeText('info@qesg.co.kr').then(() => {
                // 성공 시 토스트 메시지 표시 (구현된 EventBus 사용)
                // this.$EventBus.$emit('single-toast-on', { ... });
                this.$EventBus.$emit('toast-success', this.$t('이메일 주소 복사'), this.$t('이메일 주소가 클립보드에 복사되었습니다.'));
            }).catch(err => {
                console.error('클립보드 복사 실패:', err);
            });
        }
    },
};
</script>

<style lang="scss">
.question-blocked {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 50;
    flex-grow: 1;
    height: 100%;
    background: rgb(255, 255, 255, 0.2);
}

.question-block-alert {
    top: 0px;
    height: 60px;
    background-color: rgba(0, 0, 0, 0.5);
    color: white;
    border: 0;
    border-radius: 0;
    z-index: 60;
}

#question_infobox > a {
    color: var(--blue7);
}

.suggest-submit-alert {
    position: fixed;
    bottom: 0;
    right: 0;
    width: 100%;
}

</style>
